const REFRESH_LOCK_KEY = "token-refresh-lock";
const REFRESH_RESULT_KEY = "token-refresh-result";
const EVENT_NAME_STORAGE = "storage";
const REFRESH_LOCK_TIMEOUT = 1 * 60 * 1000; // 1 minute

type RefreshResult = { accessToken: string; isExpired: boolean };

// Instance-level memory cache
let instanceLockTimestamp: number | null = null;
let instanceResult: RefreshResult | null = null;

function now() {
	return Date.now();
}

function setLock() {
	instanceLockTimestamp = now();
	localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ timestamp: instanceLockTimestamp }));
}

function clearLock() {
	instanceLockTimestamp = null;
	localStorage.removeItem(REFRESH_LOCK_KEY);
}

function isLocked() {
	// Prefer instance-local memory for speed
	if (instanceLockTimestamp && now() - instanceLockTimestamp < REFRESH_LOCK_TIMEOUT) {
		return true;
	}
	// Fallback to localStorage for cross-instance
	const lock = localStorage.getItem(REFRESH_LOCK_KEY);
	if (!lock) return false;
	try {
		const { timestamp } = JSON.parse(lock);
		return now() - timestamp < REFRESH_LOCK_TIMEOUT;
	} catch {
		return false;
	}
}

function setResult(result: RefreshResult) {
	instanceResult = result;
	localStorage.setItem(REFRESH_RESULT_KEY, JSON.stringify({ ...result, timestamp: now() }));
}

function getResult(): RefreshResult | null {
	// Prefer instance-local memory for speed
	if (instanceResult) return instanceResult;
	const res = localStorage.getItem(REFRESH_RESULT_KEY);
	if (!res) return null;
	try {
		const { accessToken, isExpired } = JSON.parse(res);
		return { accessToken, isExpired };
	} catch {
		return null;
	}
}

export async function coordinatedRefreshToken(
	refreshFn: () => Promise<RefreshResult>,
): Promise<RefreshResult> {
	if (typeof window === "undefined") {
		// SSR: just run the refresh
		return refreshFn();
	}

	// If another instance is refreshing, wait for it
	if (isLocked()) {
		return new Promise<RefreshResult>((resolve, reject) => {
			function onStorage(e: StorageEvent) {
				if (e.key === REFRESH_RESULT_KEY) {
					window.removeEventListener(EVENT_NAME_STORAGE, onStorage);
					const result = getResult();
					if (result) resolve(result);
					else reject(new Error("No refresh result found"));
				}
			}
			window.addEventListener(EVENT_NAME_STORAGE, onStorage);
		});
	}

	// Otherwise, acquire the lock and refresh
	setLock();
	try {
		const result = await refreshFn();
		setResult(result);
		clearLock();
		// Notify other instances
		window.dispatchEvent(new StorageEvent(EVENT_NAME_STORAGE, { key: REFRESH_RESULT_KEY }));
		return result;
	} catch (err) {
		clearLock();
		throw err;
	}
}
